import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';

export interface ImageCaptureHandle {
  stopCamera: () => void;
}

interface ImageCaptureProps {
  selectedFiles: File[];
  onFilesChange: (files: File[]) => void;
  maxImages: number;
  onError?: (message: string) => void;
  onCameraStateChange?: (isActive: boolean, isMobile: boolean) => void;
}

const ImageCapture = forwardRef<ImageCaptureHandle, ImageCaptureProps>(({
  selectedFiles,
  onFilesChange,
  maxImages,
  onError,
  onCameraStateChange
}, ref) => {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onCameraStateChangeRef = useRef(onCameraStateChange);
  onCameraStateChangeRef.current = onCameraStateChange;

  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Expose stopCamera to parent via ref
  useImperativeHandle(ref, () => ({
    stopCamera: () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      setIsCameraActive(false);
    }
  }), [stream]);

  // Notify parent of camera state changes
  useEffect(() => {
    onCameraStateChangeRef.current?.(isCameraActive, isMobile);
  }, [isCameraActive, isMobile]);

  // Attach stream to video element when camera is active and stream is available
  useEffect(() => {
    if (isCameraActive && stream) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          const videoElement = videoRef.current;
          videoElement.srcObject = stream;
          videoElement.play().catch(err => {
            console.error('Failed to play video:', err);
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isCameraActive, stream]);

  const startCamera = async () => {
    try {
      onError?.('');

      let mediaStream: MediaStream | null = null;

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        });
      } catch (mobileErr) {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      }

      if (!mediaStream) {
        throw new Error('Failed to acquire camera stream');
      }

      setStream(mediaStream);
      setIsCameraActive(true);
    } catch (err) {
      onError?.('Failed to access camera. Please check permissions or use "Choose from Photos".');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onFilesChange([...selectedFiles, file]);
      }
    }, 'image/jpeg', 0.92);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const combined = [...selectedFiles, ...files].slice(0, maxImages);
      onFilesChange(combined);
      if (combined.length >= maxImages) {
        onError?.(`Maximum of ${maxImages} images allowed`);
      } else {
        onError?.('');
      }
    }
  };

  const removeImage = (index: number) => {
    onFilesChange(selectedFiles.filter((_, i) => i !== index));
    onError?.('');
  };

  return (
    <>
      {/* Full-screen camera overlay for mobile */}
      {/* visibility:visible ensures this overlay is shown even when a parent has visibility:hidden */}
      {isCameraActive && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'var(--photo-backdrop)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            visibility: 'visible'
          }}
        >
          {/* Header with photo counter and Done button */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              zIndex: 10
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 500 }}>
              {selectedFiles.length} captured
            </div>
            <button
              onClick={stopCamera}
              type="button"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: 'white',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              &#x2715;
            </button>
          </div>

          {/* Camera viewport */}
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                backgroundColor: 'var(--photo-backdrop)'
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Pending thumbnails strip */}
            {selectedFiles.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: '160px',
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                gap: '6px',
                padding: '0 16px'
              }}>
                {selectedFiles.slice(-5).map((file, index) => (
                  <div
                    key={index}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Captured ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Capture button */}
            <button
              type="button"
              onClick={capturePhoto}
              disabled={selectedFiles.length >= maxImages}
              style={{
                position: 'absolute',
                bottom: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                fontSize: '1.125rem',
                borderRadius: '50%',
                border: '4px solid white',
                width: '70px',
                height: '70px',
                fontWeight: 600,
                cursor: selectedFiles.length >= maxImages ? 'not-allowed' : 'pointer',
                opacity: selectedFiles.length >= maxImages ? 0.5 : 1,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              &#x1F4F7;
            </button>
          </div>
        </div>
      )}

      {/* Camera / Photos buttons */}
      {!isCameraActive && (
        <div style={{ padding: '16px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '8px',
            alignItems: 'center'
          }}>
            <button
              type="button"
              onClick={startCamera}
              disabled={selectedFiles.length >= maxImages}
              style={{
                width: '100%',
                backgroundColor: selectedFiles.length >= maxImages ? 'var(--disabled-bg)' : 'var(--primary)',
                color: selectedFiles.length >= maxImages ? 'var(--disabled)' : 'var(--text-inverse)',
                padding: '10px 8px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: selectedFiles.length >= maxImages ? 'not-allowed' : 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Camera
            </button>

            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '0.75rem',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}>OR</div>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={selectedFiles.length >= maxImages}
              style={{
                width: '100%',
                backgroundColor: selectedFiles.length >= maxImages ? 'var(--disabled-bg)' : 'var(--border-light)',
                color: selectedFiles.length >= maxImages ? 'var(--disabled)' : 'var(--text-primary)',
                padding: '10px 8px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: selectedFiles.length >= maxImages ? 'not-allowed' : 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Photos
            </button>
          </div>
        </div>
      )}

      {/* Desktop inline camera */}
      {isCameraActive && !isMobile && (
        <div style={{
          position: 'relative',
          backgroundColor: 'var(--photo-backdrop)',
          borderRadius: '8px',
          overflow: 'visible',
          marginBottom: '16px',
          width: '100%',
          maxWidth: '640px',
          margin: '0 auto 16px'
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: 'auto',
              minHeight: window.innerHeight > window.innerWidth ? '360px' : '60vh',
              maxHeight: window.innerHeight > window.innerWidth ? 'none' : '75vh',
              objectFit: 'cover',
              display: 'block',
              backgroundColor: 'var(--photo-backdrop)'
            }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Close button */}
          <button
            type="button"
            onClick={stopCamera}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              fontSize: '1.25rem',
              cursor: 'pointer',
              zIndex: 10
            }}
          >
            &#x2715;
          </button>

          {/* Capture button */}
          <button
            type="button"
            onClick={capturePhoto}
            disabled={selectedFiles.length >= maxImages}
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--primary)',
              color: 'var(--text-inverse)',
              padding: '12px 24px',
              fontSize: '1.125rem',
              borderRadius: '50%',
              border: '4px solid white',
              width: '70px',
              height: '70px',
              fontWeight: 600,
              cursor: selectedFiles.length >= maxImages ? 'not-allowed' : 'pointer',
              opacity: selectedFiles.length >= maxImages ? 0.5 : 1,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              zIndex: 10
            }}
          >
            &#x1F4F7;
          </button>
        </div>
      )}

      {/* Image preview grid */}
      {selectedFiles.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Captured Photos ({selectedFiles.length}/{maxImages})
            </h4>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '8px',
            marginBottom: '16px'
          }}>
            {selectedFiles.map((file, index) => {
              const isHeic = file.name.toLowerCase().endsWith('.heic') || file.type === 'image/heic' || file.type === 'image/heif';
              return (
              <div key={index} style={{ position: 'relative' }}>
                {isHeic ? (
                  <div
                    style={{
                      width: '100%',
                      height: '100px',
                      backgroundColor: 'var(--bg-secondary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      border: index === 0 ? '2px solid var(--primary)' : '1px solid var(--border-light)'
                    }}
                  >
                    <img src="/logo-192.png" alt="HEIC image" style={{ width: '36px', height: '36px', opacity: 0.35, borderRadius: '6px' }} />
                  </div>
                ) : (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Photo ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: index === 0 ? '2px solid var(--primary)' : '1px solid var(--border-light)'
                    }}
                  />
                )}
                {index === 0 && (
                  <span style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '4px',
                    backgroundColor: 'var(--primary)',
                    color: 'var(--text-inverse)',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '0.625rem',
                    fontWeight: 600
                  }}>
                    PRIMARY
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  &#x2715;
                </button>
              </div>
            );
            })}
          </div>
        </div>
      )}
    </>
  );
});

export default ImageCapture;
